import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { deslugifyTheme } from '../utils/urlUtils';

interface BreadcrumbItem {
  label: string;
  url: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  darkMode: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, darkMode }) => {
  const { theme_slug } = useParams<{ theme_slug?: string }>();

  const breadcrumbItems = theme_slug
    ? [
        { label: 'Accueil', url: '/' },
        { label: deslugifyTheme(theme_slug), url: `/themes/${theme_slug}` }
      ]
    : items;

  if (breadcrumbItems.length <= 1) return null;

  const baseText = darkMode ? 'text-gray-300' : 'text-gray-600';
  const currentText = darkMode ? 'text-white' : 'text-gray-900';
  const hoverText = darkMode ? 'hover:text-white' : 'hover:text-gray-900';
  const chevronColor = darkMode ? 'text-gray-500' : 'text-gray-400';

  return (
    <nav
      className={`flex items-center flex-nowrap gap-1.5 sm:gap-2 text-xs sm:text-sm mb-6 overflow-hidden ${baseText}`}
      aria-label="Fil d'Ariane"
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const isFirst = index === 0;

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight
                className={`h-3.5 w-3.5 flex-shrink-0 ${chevronColor}`}
                aria-hidden="true"
              />
            )}
            {isLast ? (
              <span
                className={`font-semibold truncate max-w-[55vw] sm:max-w-md ${currentText}`}
                aria-current="page"
                title={item.label}
              >
                {isFirst && (
                  <Home className="h-4 w-4 inline mr-1 align-[-2px]" aria-hidden="true" />
                )}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.url}
                className={`flex-shrink-0 truncate max-w-[90px] sm:max-w-[200px] transition-colors hover:underline ${hoverText}`}
                title={item.label}
                aria-label={isFirst ? 'Accueil' : item.label}
              >
                {isFirst ? (
                  <>
                    <Home className="h-4 w-4 inline sm:mr-1 align-[-2px]" aria-hidden="true" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </>
                ) : (
                  item.label
                )}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
